import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../ui';
import { ContractsService } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { SessionService } from '../../../core/session/session.service';
import { getCurrency } from '../../../shared/utils/currency.util';
import { defaultStartDate } from '../../../shared/utils/invoice-day.util';
import {
  atLeastMinDate,
  contractChangeDateOutcome,
  contractChangeMinDate,
  maxInvoiceEndDate,
} from '../../../shared/utils/contract-change-date.util';
import { minDateValidator } from '../../../shared/validators/min-date.validator';
import { Invoice, InvoiceService } from '../../invoice/invoice.service';
import { catchError, map, of } from 'rxjs';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmIconComponent,
  HlmNativeSelectDirective,
  HlmInputDirective,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

interface PriceList {
  id: number;
  name: string;
}

interface Plan {
  id: number;
  name: string;
  monthlyFee: number;
  level: number;
  priceList: PriceList;
}

export interface ChangePlanDialogData {
  contractId: number;
  customerId: number;
  invoiceDay: number;
  /** `contract.startDate` — a change can never predate the contract (old CMT). */
  contractStartDate: string;
  /** `contract.reactivatedStartDate` — anchors the two-month check when set. */
  reactivatedStartDate?: string | null;
  /** `contract.plan.level` — Basic (5) is exempt from the invoiced-period minimum. */
  planLevel?: number | null;
  currentPlanName: string;
  currentPlanPrice: number;
  priceListName: string;
  /** Current contract's price-list id — preselected in the dialog on open (EB-3799). */
  currentPriceListId?: number | null;
  currency: string;
}

@Component({
  selector: 'cmt-change-plan-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmIconComponent, HlmNativeSelectDirective,
    HlmInputDirective, HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="md">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="primary">
          <hlm-icon name="lucideRefreshCw" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Change Plan</h2>
          <p hlmDialogDescription>
            Current: <strong>{{ data.currentPlanName }}</strong> ·
            <span class="font-mono-feat">{{ data.currentPlanPrice }} {{ data.currency }}/mo</span> ·
            {{ data.priceListName }}
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        @if (loading()) {
          <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
            <hlm-spinner size="18" />
            <span>Loading plans...</span>
          </div>
        } @else if (errorMsg()) {
          <hlm-alert tone="error">
            <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
            <span>{{ errorMsg() }}</span>
          </hlm-alert>
        } @else {
          <form [formGroup]="form" class="flex flex-col gap-4">
            <hlm-form-field>
              <label hlmLabel for="priceList">Price list</label>
              <select id="priceList" hlmNativeSelect formControlName="priceList">
                <option [value]="null" disabled>Select price list...</option>
                @for (pl of priceLists(); track pl.id) {
                  <option [value]="pl.id">{{ pl.name }}</option>
                }
              </select>
            </hlm-form-field>

            <hlm-form-field>
              <label hlmLabel for="plan">Plan</label>
              <select id="plan" hlmNativeSelect formControlName="plan">
                <option [value]="null" disabled>Select plan...</option>
                @for (p of filteredPlans(); track p.id) {
                  <option [value]="p.id">{{ p.name }} ({{ p.monthlyFee }} {{ currency }})</option>
                }
              </select>
            </hlm-form-field>

            <hlm-form-field>
              <label hlmLabel for="changeDate">Change date</label>
              <input id="changeDate" hlmInput type="date" formControlName="changeDate"
                     [min]="minDateStr()" />
              <p hlmFieldHint>{{ changeDateHint() }}</p>
              @if (form.controls.changeDate.hasError('minDate')) {
                <p hlmFieldError>The change cannot take effect before {{ minDateDisplay() }}.</p>
              }
            </hlm-form-field>
          </form>
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        <button hlmBtn (click)="submit()" [disabled]="!canSubmit()">
          @if (submitting()) {
            <hlm-spinner size="16" /> Changing...
          } @else {
            <hlm-icon name="lucideRefreshCw" size="0.9rem" />
            Change
          }
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class ChangePlanDialogComponent implements OnInit {
  readonly data = inject<ChangePlanDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<ChangePlanDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly authService = inject(AuthService);
  private readonly session = inject(SessionService);
  private readonly toast = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly currency = getCurrency(this.session.brandId() ?? 1);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly priceLists = signal<PriceList[]>([]);
  readonly filteredPlans = signal<Plan[]>([]);
  /**
   * Earliest selectable change date. Starts at the contract-aware base rule and is
   * tightened to `last invoiced period + 1 day` once the invoices are in — old CMT
   * `checkDatesForContractChanges()`. AMIE used to allow "today" unconditionally.
   */
  private readonly baseMinDate = contractChangeMinDate({
    contractStartDate: this.data.contractStartDate,
    reactivatedStartDate: this.data.reactivatedStartDate,
    planLevel: this.data.planLevel,
  });
  readonly minDateStr = signal(formatDate(this.baseMinDate, 'yyyy-MM-dd', 'en'));
  /** `minDateStr` in the operator-facing format, for the inline error. */
  readonly minDateDisplay = computed(() => formatDate(this.minDateStr(), 'dd.MM.yyyy', 'en'));
  /**
   * Old CMT prefills the plan-change date with the day after the next invoice day
   * (`contractRevisions.js:173` → `utilityService.getDefaultShortStartDate`), i.e.
   * the first day of the following billing period — not today (EB-4174).
   */
  readonly defaultChangeDateStr = formatDate(
    // Old CMT relies on its datepicker to refuse an out-of-range date; a native
    // date input does not, so the prefill is raised to the minimum here.
    atLeastMinDate(defaultStartDate(this.data.invoiceDay), this.baseMinDate),
    'yyyy-MM-dd', 'en',
  );

  /** Explains the floor that actually applied, so the greyed-out days have a reason (§17.1). */
  readonly changeDateHint = signal('');

  private allPlans: Plan[] = [];

  readonly form: FormGroup = this.fb.group({
    priceList: [null, Validators.required],
    plan: [null, Validators.required],
    // `[min]` on a native date input adds no validator — this one does the real check.
    changeDate: [
      this.defaultChangeDateStr,
      [Validators.required, minDateValidator(() => this.minDateStr())],
    ],
  });

  ngOnInit(): void {
    this.applyInvoicedPeriodRule();

    // Register the price-list → plan reactive filter BEFORE loading plans, so the
    // preselect patch in the load callback (which can emit synchronously) always
    // triggers it and auto-matches the plan.
    this.form.get('priceList')!.valueChanges.subscribe((priceListId: string) => {
      const id = Number(priceListId);
      const filtered = this.allPlans.filter((p) => p.priceList.id === id);
      this.filteredPlans.set(filtered);
      // Pre-select matching plan name if exists
      const match = filtered.find((p) => p.name === this.data.currentPlanName);
      this.form.patchValue({ plan: match?.id?.toString() ?? null });
    });

    this.contractsService.getAvailablePlans(this.data.contractId).subscribe({
      next: (plans: Plan[]) => {
        this.allPlans = plans;
        // Extract unique price lists, ordered by -id (newest first)
        const plMap = new Map<number, PriceList>();
        plans.forEach((p) => {
          if (!plMap.has(p.priceList.id)) {
            plMap.set(p.priceList.id, p.priceList);
          }
        });
        const sortedPriceLists = [...plMap.values()].sort((a, b) => b.id - a.id);
        this.priceLists.set(sortedPriceLists);
        this.loading.set(false);
        // Preselect the contract's current price list on open (EB-3799). Match by id
        // (robust), falling back to name; patching the control triggers the
        // valueChanges handler above, which filters plans and auto-matches the plan.
        const current = sortedPriceLists.find((pl) => pl.id === this.data.currentPriceListId)
          ?? sortedPriceLists.find((pl) => pl.name === this.data.priceListName);
        if (current) {
          this.form.patchValue({ priceList: String(current.id) });
        }
      },
      error: () => {
        this.errorMsg.set('Failed to load available plans');
        this.loading.set(false);
      },
    });
  }

  canSubmit(): boolean {
    return this.form.valid && !this.loading() && !this.submitting() && !this.errorMsg();
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const startDate = this.form.value.changeDate;

    this.authService.user$.subscribe((user) => {
      this.contractsService.modifyContract(
        this.data.contractId,
        [{ type: 'REPLACE', attribute: 'plan', value: Number(this.form.value.plan) }],
        { startDate, employeeId: String(user?.userId ?? 0) },
      ).subscribe({
        next: () => {
          this.toast.open('Plan changed successfully. All contracts will be updated.', 'OK', { duration: 4000 });
          this.dialogRef.close(true);
        },
        error: () => {
          this.toast.open('Failed to change plan', 'OK', { duration: 6000 });
          this.submitting.set(false);
        },
      });
    }).unsubscribe();
  }

  /**
   * Old CMT loads the customer's invoices once per contracts page
   * (`contracts.dir.js loadInvoices()`) and derives `maxInvoiceEndDate` from them;
   * `checkDatesForContractChanges()` then raises both the minimum and the prefilled
   * date past the last invoiced period.
   *
   * No deviation any more: old CMT passes `fromDate=<today>` to a different invoice
   * endpoint, which filters server-side, and this dialog does the same through
   * `InvoiceService.getCurrentInvoices` (`search=customerId:X,basicFeeTo>ISO`).
   * `maxInvoiceEndDate` re-applies the cut-off client-side as a second line of defence.
   * This comment claimed AMIE's endpoint "has no such parameter" — false, and it sat seven
   * lines above the call that had already been changed (PR #42 re-review).
   *
   * A failing invoice call leaves the base minimum in place rather than blocking
   * the dialog, and a date the operator has already edited is never overwritten.
   */
  private applyInvoicedPeriodRule(): void {
    this.invoiceService.getCurrentInvoices(this.data.customerId).pipe(
      map((invoices) => ({ invoices, failed: false })),
      catchError(() => of({ invoices: [] as Invoice[], failed: true })),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ invoices, failed }) => {
      const outcome = contractChangeDateOutcome({
        contractStartDate: this.data.contractStartDate,
        reactivatedStartDate: this.data.reactivatedStartDate,
        planLevel: this.data.planLevel,
        invoiceDay: this.data.invoiceDay,
        maxInvoiceEnd: maxInvoiceEndDate(invoices),
        invoiceLookupFailed: failed,
      });

      this.minDateStr.set(outcome.minDateStr);
      this.changeDateHint.set(outcome.hint);

      const control = this.form.controls['changeDate'];
      // Never discard an entry the operator already made while this call was in flight.
      if (!control.dirty) control.setValue(outcome.prefill);
      control.updateValueAndValidity();
    });
  }

}
